import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Clock, AlertCircle, ArrowDown } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface Task {
  id: string;
  title: string;
  status: string;
  created_at: string;
  progress_percentage: number;
  detailed_status?: {
    stage: string;
    details: string;
  };
  dependencies?: string[];
}

interface TaskListProps {
  tasks?: Task[];
  isLoading: boolean;
}

export const TaskList = ({ tasks, isLoading }: TaskListProps) => {
  const [taskProgress, setTaskProgress] = useState<Record<string, number>>({});
  const [localTasks, setLocalTasks] = useState<Task[]>([]);

  useEffect(() => {
    if (tasks) {
      // Filter out duplicate tasks based on title and ensure proper order
      const uniqueTasks = tasks.reduce((acc: Task[], current) => {
        const exists = acc.find(task => task.title === current.title);
        if (!exists) {
          acc.push(current);
        }
        return acc;
      }, []);
      setLocalTasks(uniqueTasks);
    }
  }, [tasks]);

  useEffect(() => {
    console.log("Setting up task progress subscription");
    const channel = supabase
      .channel('task-progress')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tasks'
        },
        (payload: any) => {
          console.log("Received task update:", payload);
          if (payload.new) {
            setTaskProgress(prev => ({
              ...prev,
              [payload.new.id]: payload.new.progress_percentage || 0
            }));
            
            // Update local tasks when receiving updates
            setLocalTasks(prevTasks => {
              const updatedTasks = [...prevTasks];
              const taskIndex = updatedTasks.findIndex(t => t.id === payload.new.id);
              
              if (taskIndex >= 0) {
                updatedTasks[taskIndex] = {
                  ...updatedTasks[taskIndex],
                  ...payload.new
                };
              } else if (payload.eventType === 'INSERT') {
                // Only add new task if it's not a duplicate
                const isDuplicate = updatedTasks.some(t => t.title === payload.new.title);
                if (!isDuplicate) {
                  updatedTasks.push(payload.new);
                }
              }

              // Sort tasks based on dependencies and predefined order
              return sortTasks(updatedTasks);
            });
          }
        }
      )
      .subscribe((status) => {
        console.log("Task progress subscription status:", status);
      });

    return () => {
      console.log("Cleaning up task progress subscription");
      supabase.removeChannel(channel);
    };
  }, []);

  // Helper function to sort tasks based on dependencies and predefined order
  const sortTasks = (tasksToSort: Task[]): Task[] => {
    const taskOrder = [
      "Data Collection",
      "Content Analysis",
      "Weekly Topic Planner",
      "Content Creation Agent",
      "Quality Control Agent",
      "Content Quality Analyzer",
      "Newsletter Generation"
    ];

    return [...tasksToSort].sort((a, b) => {
      return taskOrder.indexOf(a.title) - taskOrder.indexOf(b.title);
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    );
  }

  const displayTasks = localTasks?.length ? localTasks : [];

  if (!displayTasks.length) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          No tasks found
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {sortTasks(localTasks).map((task, index) => (
        <div key={task.id}>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {task.status === 'completed' ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  ) : task.status === 'processing' ? (
                    <Clock className="h-5 w-5 text-yellow-500" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-red-500" />
                  )}
                  <span className="font-medium">{task.title}</span>
                </div>
                <Badge 
                  variant={
                    task.status === 'completed' ? "default" : 
                    task.status === 'processing' ? "secondary" : 
                    "destructive"
                  }
                >
                  {task.status === 'processing' ? `${taskProgress[task.id] || task.progress_percentage || 0}%` : task.status}
                </Badge>
              </div>
              <Progress 
                value={taskProgress[task.id] || task.progress_percentage || 0} 
                className="h-2"
              />
              {task.detailed_status && (
                <div className="mt-2 text-sm text-muted-foreground">
                  <p>{task.detailed_status.stage}: {task.detailed_status.details}</p>
                </div>
              )}
            </CardContent>
          </Card>
          {index < displayTasks.length - 1 && (
            <div className="flex justify-center my-2">
              <ArrowDown className="h-6 w-6 text-muted-foreground" />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
